import { useTranslation } from 'react-i18next';

export interface OrganizationSchema {
  "@context": string;
  "@type": string;
  name: string;
  description: string;
  url: string;
  logo: string;
  contactPoint: {
    "@type": string;
    contactType: string;
    email: string;
  };
  sameAs: string[];
}

export interface ProfessionalServiceSchema {
  "@context": string;
  "@type": string;
  name: string;
  description: string;
  provider: {
    "@type": string;
    name: string;
    description: string;
  };
  areaServed: string;
  serviceType: string;
  availableLanguage: string[];
}

export interface PersonSchema {
  "@context": string;
  "@type": string;
  name: string;
  description: string;
  jobTitle: string;
  worksFor: {
    "@type": string;
    name: string;
  };
  knowsAbout: string[];
  availableLanguage: string[];
}

export const useStructuredData = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const getOrganizationSchema = (): OrganizationSchema => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Resonow",
    description: isZh 
      ? "Resonow是一个专业的疗愈师平台，提供能量疗愈、心理咨询、传统中医等多种疗愈服务。"
      : "Resonow is a professional healing platform offering energy healing, psychological counseling, traditional Chinese medicine, and various healing services.",
    url: window.location.origin,
    logo: `${window.location.origin}/logo.png`,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "info@resonow.org"
    },
    sameAs: [
      "https://www.facebook.com/resonow",
      "https://www.instagram.com/resonow",
      "https://www.linkedin.com/company/resonow"
    ]
  });

  const getProfessionalServiceSchema = (): ProfessionalServiceSchema => ({
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: isZh ? "专业疗愈服务" : "Professional Healing Services",
    description: isZh 
      ? "提供能量疗愈、心理咨询、传统中医、瑜伽、冥想、芳香疗法等专业疗愈服务"
      : "Providing professional healing services including energy healing, psychological counseling, traditional Chinese medicine, yoga, meditation, and aromatherapy",
    provider: {
      "@type": "Organization",
      name: "Resonow",
      description: isZh 
        ? "专业疗愈师平台" 
        : "Professional Healing Platform"
    },
    areaServed: isZh ? "全球" : "Global",
    serviceType: isZh ? "疗愈服务" : "Healing Services",
    availableLanguage: ["en", "zh"]
  });

  const getPersonSchema = (practitioner: any): PersonSchema => ({
    "@context": "https://schema.org",
    "@type": "Person",
    name: `${practitioner.first_name} ${practitioner.last_name}`,
    description: practitioner.bio || (isZh ? "专业疗愈师" : "Professional Healer"),
    jobTitle: practitioner.professional_title || (isZh ? "疗愈师" : "Healer"),
    worksFor: {
      "@type": "Organization",
      name: "Resonow"
    },
    knowsAbout: practitioner.specialties || [],
    availableLanguage: practitioner.languages_spoken || ["en", "zh"]
  });

  const getCertificationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Course",
    name: isZh ? "疗愈师认证体系" : "Healer Certification System",
    description: isZh 
      ? "Resonow平台的专业疗愈师认证体系，包含能量疗愈、心理咨询、传统中医等多个专业认证"
      : "Professional healer certification system on Resonow platform, including energy healing, psychological counseling, traditional Chinese medicine and other professional certifications",
    provider: {
      "@type": "Organization",
      name: "Resonow"
    },
    educationalCredentialAwarded: isZh ? "专业疗愈师认证" : "Professional Healer Certification",
    courseMode: ["online", "offline"],
    availableLanguage: ["en", "zh"]
  });

  return {
    getOrganizationSchema,
    getProfessionalServiceSchema,
    getPersonSchema,
    getCertificationSchema
  };
};